/* Peter Suchy Jewelers — global theme JS (vanilla, no dependencies). */
(() => {
  'use strict';

  /* ---------- Dialog toggles (search, mobile drawer, offer modal) ---------- */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[aria-controls]');
    if (toggle) {
      const dialog = document.getElementById(toggle.getAttribute('aria-controls'));
      if (dialog instanceof HTMLDialogElement) {
        event.preventDefault();
        dialog.showModal();
        toggle.setAttribute('aria-expanded', 'true');
        dialog.addEventListener(
          'close',
          () => toggle.setAttribute('aria-expanded', 'false'),
          { once: true }
        );
        const input = dialog.querySelector('input[type="search"], input[type="email"], input[type="text"]');
        if (input) setTimeout(() => input.focus(), 50);
        return;
      }
    }

    const closer = event.target.closest('[data-close-dialog]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }

    // Close a dialog when its backdrop is clicked
    if (event.target instanceof HTMLDialogElement) {
      const rect = event.target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!inside) event.target.close();
    }
  });

  /* ---------- Cart drawer ---------- */
  const cartDrawer = () => document.getElementById('CartDrawer');

  // Line changes, stock caps, errors and focus for the drawer and the cart
  // page live in cart.js (window.psjCart), which both cart sections load.
  // In "page" cart mode it is only on /cart, so add to cart below also works
  // without it.

  /* ---------- Toast ---------- */
  let toastTimer;
  window.psjToast = (html) => {
    const toast = document.getElementById('Toast');
    if (!toast) return;
    toast.innerHTML = html;
    toast.hidden = false;
    requestAnimationFrame(() => toast.classList.add('is-open'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toast.classList.remove('is-open');
      setTimeout(() => (toast.hidden = true), 350);
    }, 5000);
  };

  /* ---------- Cart helpers ---------- */
  // Also announces the new count as a `psj:cart:updated` event, so other
  // code (e.g. the header's bag label) can follow it without hooking in here.
  /* ---------- Untitled third-party frames ----------
     Shopify's web-pixel sandbox (a hidden analytics frame) and Google Pay's
     frames arrive with no title, which accessibility checkers flag (2.4.1).
     Name them; the hidden helpers are also taken out of assistive tech. */
  const FRAMES = [
    { match: (f) => f.id.startsWith('web-pixel-sandbox'), title: 'Shopify analytics', hidden: true },
    { match: (f) => f.name.startsWith('__detect_close'), title: 'Google Pay helper', hidden: true },
    { match: (f) => /^https:\/\/pay\.google\.com\//.test(f.src), title: 'Google Pay' },
  ];
  const nameFrames = () => {
    document.querySelectorAll('iframe:not([title])').forEach((frame) => {
      const known = FRAMES.find((entry) => entry.match(frame));
      if (!known) return;
      frame.title = known.title;
      if (known.hidden) {
        frame.setAttribute('aria-hidden', 'true');
        frame.tabIndex = -1;
      }
    });
  };
  nameFrames();
  new MutationObserver(nameFrames).observe(document.body, { childList: true, subtree: true });

  window.psjUpdateCartCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
    document.dispatchEvent(new CustomEvent('psj:cart:updated', { detail: { itemCount: count } }));
  };

  /* ---------- Quantity steppers ---------- */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('.quantity__button');
    if (!button) return;
    const input = button.closest('.quantity')?.querySelector('.quantity__input');
    if (!input) return;
    const step = button.dataset.direction === 'down' ? -1 : 1;
    const min = parseInt(input.min || '1', 10);
    const next = Math.max(min, (parseInt(input.value, 10) || min) + step);
    if (input.value !== String(next)) {
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });

  /* ---------- Deferred media (YouTube/Vimeo posters) ---------- */
  document.addEventListener('click', (event) => {
    const poster = event.target.closest('[data-deferred-media]');
    if (!poster) return;
    const template = poster.querySelector('template');
    if (!template) return;
    poster.innerHTML = template.innerHTML;
    poster.removeAttribute('data-deferred-media');
  });

  /* ---------- Product forms: AJAX add to cart ---------- */
  const pendingForms = new WeakSet();

  const escapeHtml = (text) => String(text ?? '').replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);

  const parseSection = (html) => new DOMParser().parseFromString(html || '', 'text/html');

  // The Cart API's items[] shape, on purpose: a bare id/quantity add skips
  // Shopify's stock check (that is how a one-of-a-kind ring went into the bag
  // twice), while items[] enforces it and answers 422.
  const cartItemFrom = (form) => {
    const data = new FormData(form);
    const item = {
      id: Number(data.get('id')),
      quantity: Math.max(1, parseInt(data.get('quantity'), 10) || 1),
    };
    const properties = {};
    data.forEach((value, name) => {
      const match = name.match(/^properties\[(.+)\]$/);
      if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
    });
    if (Object.keys(properties).length) item.properties = properties;
    if (data.get('selling_plan')) item.selling_plan = Number(data.get('selling_plan'));
    return item;
  };

  // The drawer section is requested even when there's no drawer: it carries
  // the bag count, and each line's stock cap and messages.
  const addSections = () => window.psjCart?.sectionIds(['cart-drawer']) || 'cart-drawer';

  const fetchAddSections = async () => {
    try {
      const ids = encodeURIComponent(addSections());
      // No JSON Accept header: with one, /cart answers as /cart.js instead.
      const response = await fetch(`${window.psjRoutes.cart_url}?sections=${ids}`);
      return response.ok ? await response.json() : null;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const syncCart = (sections) => {
    if (!sections) return;
    if (window.psjCart) {
      window.psjCart.renderViews(sections);
      return;
    }
    const inner = parseSection(sections['cart-drawer']).querySelector('[data-cart-inner]');
    const count = parseInt(inner?.dataset.itemCount, 10);
    if (Number.isFinite(count)) window.psjUpdateCartCount(count);
  };

  const toast = (message, withBagLink) =>
    window.psjToast(
      `<span>${escapeHtml(message)}</span>` +
        (withBagLink ? `<a href="${window.psjRoutes.cart_url}">${escapeHtml(window.psjStrings.viewCart)}</a>` : '')
    );

  const onAdded = async (data) => {
    syncCart(data.sections || (await fetchAddSections()));
    const title = data.items?.[0]?.product_title || '';
    const message = window.psjStrings.addedToCart.replace('{{ title }}', title);
    if (window.psjCart?.openDrawer()) window.psjCart.announce(cartDrawer(), message);
    else toast(message, true);
  };

  // Refused (422): sold out, or the bag already holds every one there is.
  // For the second, say so plainly and show the bag.
  const onRefused = async (item, data) => {
    const sections = await fetchAddSections();
    const line = parseSection(sections?.['cart-drawer']).querySelector(
      `[data-cart-line][data-variant-id="${item.id}"]`
    );
    const message = line?.dataset.inBagMessage || data.description || data.message;
    syncCart(sections);

    const cart = window.psjCart;
    const drawer = cartDrawer();
    if (line && cart && drawer) {
      cart.showNotice(drawer, message);
      cart.openDrawer();
      cart.announce(drawer, message);
    } else {
      toast(message, Boolean(line));
    }
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-ajax-cart]');
    if (!form) return;
    event.preventDefault();

    // One add per form at a time: aria-disabled alone didn't stop a double
    // click, and each click added the piece again.
    const submitButton = event.submitter || form.querySelector('[type="submit"]');
    if (pendingForms.has(form) || submitButton?.getAttribute('aria-disabled') === 'true') return;
    pendingForms.add(form);

    const originalText = submitButton?.textContent;
    if (submitButton) {
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.setAttribute('aria-busy', 'true');
      submitButton.textContent = submitButton.dataset.addingText || originalText;
    }

    // With no answer from the Cart API at all, post the form the ordinary way.
    let leaving = false;
    const postNatively = () => {
      leaving = true;
      HTMLFormElement.prototype.submit.call(form);
    };

    const item = cartItemFrom(form);
    let response = null;
    try {
      response = await fetch(`${window.psjRoutes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item], sections: addSections() }),
      });
    } catch (error) {
      console.error(error);
      postNatively();
    }

    try {
      if (response) {
        const data = await response.json().catch(() => null);
        if (response.ok) {
          // The diamond mark arcs into the bag first; the count and drawer
          // follow its landing (motion.js; resolves at once without motion).
          await window.psjMotion?.flyToBag(submitButton);
          await onAdded(data || {});
        }
        else if (response.status === 422 && data) await onRefused(item, data);
        else if (data?.description) toast(data.description, false);
        else postNatively();
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (!leaving) {
        pendingForms.delete(form);
        if (submitButton) {
          submitButton.removeAttribute('aria-disabled');
          submitButton.removeAttribute('aria-busy');
          submitButton.textContent = originalText;
        }
      }
    }
  });
})();
